import { useEffect, useState } from "react";
import {
  fetchProductCategories,
  saveReportWithResponse,
  getProducerById,
  confirmReport,
  fetchReportsByProducer,
  getReportById,
} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import type {
  ProductCategory,
  ReportMode,
  ProducerDetails,
  ReportSummary,
} from "@/ReactInit/Scripts/DashboardPage/types";
import { DashboardCard } from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import {
  DashboardButton,
  DashboardIconButton,
} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import { DashboardAlertBox } from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import ProductSelector from "@/ReactInit/Scripts/DashboardPage/Actor/components/ProductSelector";
import DashboardReportSummaryView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardReportSummaryView";
import DashboardReportYearSelector from "@/ReactInit/Scripts/DashboardPage/components/DashboardReportYearSelector";
import { withMinDuration } from "@/ReactInit/Scripts/DashboardPage/utils/minDuration";
import {
  getDisabledCategoriesForIndex,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

type EditableCategoryBlock = {
  id: string;
  category: string | null;
  products: string[];
  volumes: string[];
  lastYearVolumes: (number | null)[];
  numberIds: (string | undefined)[];
};

const ensureArrayLength = <T,>(arr: T[], length: number, fill: T): T[] => {
  if (arr.length >= length) return arr;
  return [...arr, ...Array(length - arr.length).fill(fill)];
};

interface DashboardReportCreateViewProps {
  producerId: string;
  year: number;
  onYearChange: (year: number) => void;
  onCancel: () => void;
  onCreated?: (reportId: string) => void;
  onConfirmed?: (reportId: string) => void | Promise<void>;
}

export default function DashboardReportCreateView({
  producerId,
  year,
  onYearChange,
  onCancel,
  onCreated,
  onConfirmed,
}: DashboardReportCreateViewProps) {
  const [producer, setProducer] = useState<ProducerDetails | null>(null);
  const [producerError, setProducerError] = useState<string | null>(null);

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  const [reportId, setReportId] = useState<string | null>(null);

  const [editingCategories, setEditingCategories] = useState<
    EditableCategoryBlock[]
  >([]);
  const [rowErrorsByBlock, setRowErrorsByBlock] = useState<
    Record<string, boolean[]>
  >({});

  const [lastYearVolumesByProduct, setLastYearVolumesByProduct] = useState<
    Record<string, number>
  >({});
  const [producerReports, setProducerReports] = useState<ReportSummary[]>([]);
  const [isLoadingExistingReports, setIsLoadingExistingReports] =
    useState(true);

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const hasRowErrors = Object.values(rowErrorsByBlock).some((arr) =>
    arr.some(Boolean)
  );
  const existingReportYears = new Set(
    producerReports
      .filter((r) => r.id !== reportId)
      .map((r) => r.year)
  );
  const hasExistingReportForYear = existingReportYears.has(year);
  const duplicateYearError = `Det finnes allerede en rapport for ${year}. Velg et annet år for å fortsette.`;

  const [step, setStep] = useState<ReportMode>("edit");
  const [comment, setComment] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setProducerError(null);
        const p = await getProducerById(producerId);
        if (cancelled) return;
        setProducer(p);
      } catch (e) {
        console.error(e);
        if (!cancelled) setProducerError("Kunne ikke hente virksomhetsinfo.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [producerId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setCategoriesError(null);
        const productCategories = await fetchProductCategories();
        if (cancelled) return;

        setCategories(productCategories);
        setEditingCategories([
          {
            id: `new-cat-${Date.now()}`,
            category: null,
            products: [""],
            volumes: [""],
            lastYearVolumes: [null],
            numberIds: [undefined],
          },
        ]);
      } catch (e) {
        console.error(e);
        if (!cancelled)
          setCategoriesError("Kunne ikke hente produktkategorier.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [producerId]);

  useEffect(() => {
    let cancelled = false;

    const loadExistingReports = async () => {
      try {
        setIsLoadingExistingReports(true);
        const list = await fetchReportsByProducer(producerId);

        if (cancelled) return;
        setProducerReports(list);
      } catch (e) {
        console.error(e);
        if (cancelled) return;
        setProducerReports([]);
      } finally {
        if (!cancelled) setIsLoadingExistingReports(false);
      }
    };

    void loadExistingReports();

    return () => {
      cancelled = true;
    };
  }, [producerId]);

  useEffect(() => {
    let cancelled = false;

    const loadLastYear = async () => {
      if (!producerId || !year) return;

      try {
        const prev = producerReports.find((r) => r.year === year - 1);
        if (!prev) {
          if (!cancelled) setLastYearVolumesByProduct({});
          return;
        }

        const prevDetails = await getReportById(prev.id);

        const map: Record<string, number> = {};
        (prevDetails.numbers ?? []).forEach((n) => {
          const k = normalizeProductKey(n.productName ?? "");
          if (!k) return;
          map[k] = (map[k] ?? 0) + n.volume;
        });

        if (!cancelled) setLastYearVolumesByProduct(map);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLastYearVolumesByProduct({});
      }
    };

    void loadLastYear();

    return () => {
      cancelled = true;
    };
  }, [producerId, year, producerReports]);

  const handleAddCategoryBlock = () => {
    if (hasExistingReportForYear || isLoadingExistingReports) return;
    if (categories.length === 0) return;

    setEditingCategories((prev) => {
      const used = new Set(
        prev
          .map((b) => b.category)
          .filter(
            (c): c is string => typeof c === "string" && c.trim().length > 0
          )
      );

      const hasRemaining = categories.some((c) => !used.has(c.category));
      if (!hasRemaining) return prev;

      return [
        ...prev,
        {
          id: `new-cat-${Date.now()}`,
          category: null,
          products: [""],
          volumes: [""],
          lastYearVolumes: [null],
          numberIds: [undefined],
        },
      ];
    });
  };

  const removeCategoryBlock = (blockId: string) => {
    setEditingCategories((prev) => prev.filter((b) => b.id !== blockId));
    setRowErrorsByBlock((prev) => {
      const copy = { ...prev };
      delete copy[blockId];
      return copy;
    });
  };

  const updateCategory = (blockIndex: number, category: string | null) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;
        if ((block.category ?? "") === (category ?? "")) return block;

        return {
          ...block,
          category,
          products: [""],
          volumes: [""],
          lastYearVolumes: [null],
          numberIds: [undefined],
        };
      })
    );
  };

  const updateProduct = (
    blockIndex: number,
    rowIndex: number,
    product: string
  ) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        const targetLength = rowIndex + 1;
        const products = ensureArrayLength(block.products, targetLength, "");
        const volumes = ensureArrayLength(block.volumes, targetLength, "");
        const lastYearVolumes = ensureArrayLength(
          block.lastYearVolumes,
          targetLength,
          null
        );
        const numberIds = ensureArrayLength(
          block.numberIds,
          targetLength,
          undefined
        );

        products[rowIndex] = product;

        return { ...block, products, volumes, lastYearVolumes, numberIds };
      })
    );
  };

  const updateVolume = (
    blockIndex: number,
    rowIndex: number,
    volume: string
  ) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        const targetLength = rowIndex + 1;
        const products = ensureArrayLength(block.products, targetLength, "");
        const volumes = ensureArrayLength(block.volumes, targetLength, "");
        const lastYearVolumes = ensureArrayLength(
          block.lastYearVolumes,
          targetLength,
          null
        );
        const numberIds = ensureArrayLength(
          block.numberIds,
          targetLength,
          undefined
        );

        volumes[rowIndex] = volume;

        return { ...block, products, volumes, lastYearVolumes, numberIds };
      })
    );
  };

  const buildNumbersPayload = () => {
    return editingCategories
      .flatMap((block) =>
        block.products.map((productName, index) => {
          const volumeRaw = block.volumes[index] ?? "";
          const volumeStr = String(volumeRaw)
            .replace(/\s/g, "")
            .replace(",", ".");
          const volumeNumber = volumeStr === "" ? NaN : Number(volumeStr);

          return {
            numberId: block.numberIds[index],
            reportId: reportId ?? "",
            productName,
            volume: volumeNumber,
          };
        })
      )
      .filter(
        (n) =>
          n.productName.trim() !== "" &&
          !Number.isNaN(n.volume) &&
          n.volume !== undefined &&
          n.volume !== null
      );
  };

  const saveDraft = async (): Promise<string | null> => {
    if (isSavingDraft || isConfirming) return null;

    if (isLoadingExistingReports) {
      setActionError("Sjekker om valgt år allerede har en rapport.");
      return null;
    }

    try {
      setIsSavingDraft(true);
      setActionError(null);

      let latestReports: ReportSummary[];
      try {
        latestReports = await fetchReportsByProducer(producerId);
      } catch (e) {
        console.error(e);
        setActionError(
          "Kunne ikke kontrollere eksisterende rapporter for virksomheten."
        );
        return null;
      }

      setProducerReports(latestReports);

      const hasDuplicateInLatest = latestReports.some(
        (r) => r.year === year && r.id !== reportId
      );

      if (hasDuplicateInLatest) {
        setActionError(duplicateYearError);
        return null;
      }

      const numbers = buildNumbersPayload();

      const res = await saveReportWithResponse({
        reportId: reportId ?? "",
        producerId,
        producerName: producer?.producerName ?? "",
        orgnr: producer?.orgnr ?? "",
        year,
        comment: comment.trim() ? comment : null,
        reportedBy: "OFG Admin",
        numbers,
      });

      const newId = res.reportId;

      if (!reportId) {
        setReportId(newId);
        onCreated?.(newId);
      }

      return newId;
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error ? e.message : "Noe gikk galt ved lagring av rapport."
      );
      return null;
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleNext = async () => {
    if (hasRowErrors) return;
    const id = await withMinDuration(() => saveDraft());
    if (!id) return;
    setStep("summary");
  };

  const handleSubmitFinal = async () => {
    if (hasRowErrors) return;

    const id = await saveDraft();
    if (!id) return;

    try {
      setIsConfirming(true);
      setActionError(null);

      await withMinDuration(() =>
        confirmReport({
          reportId: id,
          reporterName: "OFG Admin",
          phoneNumber: "",
        })
      );

      await onConfirmed?.(id);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved bekreftelse av rapporten."
      );
    } finally {
      setIsConfirming(false);
    }
  };

  if (step === "summary") {
    return (
      <DashboardReportSummaryView
        year={year}
        blocks={editingCategories}
        reportComment={comment}
        setReportComment={setComment}
        onPrev={() => setStep("edit")}
        onSubmit={handleSubmitFinal}
        submitLoading={isSavingDraft || isConfirming}
      />
    );
  }

  const used = new Set(
    editingCategories
      .map((b) => b.category)
      .filter((c): c is string => typeof c === "string" && c.trim().length > 0)
  );

  const canAddMoreCategories = categories.some((c) => !used.has(c.category));

  return (
    <div className="flex flex-col gap-5 w-full mx-auto max-w-[932px]">
      {categoriesError && (
        <DashboardAlertBox type="warning" label={categoriesError} />
      )}
      {actionError && <DashboardAlertBox type="error" label={actionError} />}
      {producerError && (
        <DashboardAlertBox type="warning" label={producerError} />
      )}

      <h2 className="text-3xl font-semibold">{year}-rapport</h2>
      {hasExistingReportForYear && (
        <DashboardAlertBox type="warning" label={duplicateYearError} />
      )}

      <div className="flex flex-col gap-4">
        {editingCategories.map((block, i) => {
          const selectedCategories = editingCategories.map((b) => b.category);
          const disabledCategories = getDisabledCategoriesForIndex(
            selectedCategories,
            i
          );

          return (
            <DashboardCard as="section" key={block.id} className="relative">
              <ProductSelector
                selectedCategory={block.category}
                disabledCategories={disabledCategories}
                products={block.products}
                volumes={block.volumes}
                reportYear={year}
                lastYearVolumes={block.lastYearVolumes}
                lastYearVolumesByProduct={lastYearVolumesByProduct}
                onCategoryChange={(category) => updateCategory(i, category)}
                onProductChange={(rowIndex, product) =>
                  updateProduct(i, rowIndex, product)
                }
                onVolumeChange={(rowIndex, volume) =>
                  updateVolume(i, rowIndex, volume)
                }
                onRowErrorStateChange={(rowIndex, hasError) => {
                  setRowErrorsByBlock((prev) => {
                    const updated = { ...prev };
                    const arr = updated[block.id] ? [...updated[block.id]] : [];
                    arr[rowIndex] = hasError;
                    updated[block.id] = arr;
                    return updated;
                  });
                }}
                headerRightSlot={
                  <DashboardReportYearSelector
                    value={year}
                    onChange={onYearChange}
                    disabledYears={Array.from(existingReportYears)}
                    disabledYearReason="Det finnes allerede en rapport for dette året."
                    disabled={
                      isSavingDraft || isConfirming || reportId !== null
                    }
                  />
                }
              />

              {editingCategories.length > 1 && (
                <DashboardIconButton
                  label="Fjern produktkategori"
                  variant="sidebar"
                  rounded="full"
                  size="3"
                  icon="icon-x"
                  onClick={() => removeCategoryBlock(block.id)}
                  className="absolute top-2 right-2"
                />
              )}
            </DashboardCard>
          );
        })}

        <DashboardButton
          label="Legg til produktkategori"
          variant="sidebar"
          onClick={handleAddCategoryBlock}
          disabled={
            !canAddMoreCategories ||
            hasExistingReportForYear ||
            isLoadingExistingReports
          }
          icon="icon-circle-plus"
          className="!rounded-[100px] !py-6"
        />
      </div>

      <div className="flex flex-col tablet:flex-row justify-center gap-3 mt-4">
        <DashboardButton
          label="Avbryt"
          variant="underline"
          onClick={onCancel}
          icon="icon-x"
          disabled={isSavingDraft || isConfirming}
        />

        <DashboardButton
          label="Lagre utkast"
          variant="underline"
          onClick={() => void saveDraft()}
          icon="icon-save"
          loading={isSavingDraft}
          loadingText="Lagrer…"
          disabled={
            isSavingDraft ||
            isConfirming ||
            hasRowErrors ||
            hasExistingReportForYear ||
            isLoadingExistingReports
          }
        />

        <DashboardButton
          label="Neste"
          variant="darkplain"
          onClick={handleNext}
          icon="icon-arrow-right"
          loading={isSavingDraft}
          loadingText="Neste"
          disabled={
            isSavingDraft ||
            isConfirming ||
            hasRowErrors ||
            !producer ||
            hasExistingReportForYear ||
            isLoadingExistingReports
          }
        />
      </div>
    </div>
  );
}
